import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '../components/ui/alert-dialog';
import { ArrowLeft, GripVertical, Plus, Trash2, ChevronRight, ChevronDown, Save, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MenuFormInline = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    handle: '',
    location: 'header',
    items: []
  });

  const [loading, setLoading] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const getAllItemIds = useCallback((items, ids = []) => {
    items.forEach(item => {
      if (item.id) ids.push(item.id);
      if (item.children && item.children.length > 0) {
        getAllItemIds(item.children, ids);
      }
    });
    return ids;
  }, []);

  const fetchMenu = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/menus/${id}`);
      
      // Ensure all items have children array initialized
      const normalizeItems = (items) => {
        return items.map(item => ({
          ...item,
          children: Array.isArray(item.children) ? normalizeItems(item.children) : []
        }));
      };
      
      const normalizedData = {
        ...response.data,
        items: normalizeItems(response.data.items || [])
      };
      
      setFormData(normalizedData);
      // Expand all items by default
      const allIds = getAllItemIds(normalizedData.items);
      setExpandedItems(new Set(allIds));
    } catch (error) {
      console.error('Error fetching menu:', error);
      toast.error('Failed to load menu');
    }
  }, [id, getAllItemIds]);

  useEffect(() => {
    if (isEdit) {
      fetchMenu();
    }
  }, [isEdit, fetchMenu]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a menu name');
      return;
    }

    try {
      setLoading(true);
      if (isEdit) {
        await axios.put(`${API_URL}/api/menus/${id}`, formData);
        toast.success('Menu updated successfully');
      } else {
        await axios.post(`${API_URL}/api/menus/`, formData);
        toast.success('Menu created successfully');
      }
      navigate('/menus');
    } catch (error) {
      console.error('Error saving menu:', error);
      toast.error(error.response?.data?.detail || 'Failed to save menu');
    } finally {
      setLoading(false);
    }
  };

  const generateHandle = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').trim();
  };

  const addNewItem = (parentId = null) => {
    const newItem = {
      id: `item-${Date.now()}`,
      title: 'New Menu Item',
      url: '',
      type: 'custom',
      target: '_self',
      children: []
    };

    if (parentId) {
      setFormData({
        ...formData,
        items: addChildToItem(formData.items, parentId, newItem)
      });
      // Expand parent
      const newExpanded = new Set(expandedItems);
      newExpanded.add(parentId);
      setExpandedItems(newExpanded);
    } else {
      setFormData({
        ...formData,
        items: [...formData.items, newItem]
      });
    }
    
    // Start editing the new item
    setEditingItemId(newItem.id);
  };

  const addChildToItem = (items, parentId, newItem) => {
    return items.map(item => {
      if (item.id === parentId) {
        return {
          ...item,
          children: [...(item.children || []), newItem]
        };
      }
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: addChildToItem(item.children, parentId, newItem)
        };
      }
      return item;
    });
  };

  const updateItemInTree = (items, itemId, updates) => {
    return items.map(item => {
      if (item.id === itemId) {
        return { ...item, ...updates };
      }
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: updateItemInTree(item.children, itemId, updates)
        };
      }
      return item;
    });
  };

  const confirmDeleteItem = (itemId, parentId = null) => {
    setItemToDelete({ itemId, parentId });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmed = () => {
    if (!itemToDelete) return;

    const { itemId, parentId } = itemToDelete;

    if (parentId) {
      setFormData({
        ...formData,
        items: deleteChildFromItem(formData.items, parentId, itemId)
      });
    } else {
      setFormData({
        ...formData,
        items: formData.items.filter(item => item.id !== itemId)
      });
    }
    
    toast.success('Menu item deleted');
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const deleteChildFromItem = (items, parentId, itemId) => {
    return items.map(item => {
      if (item.id === parentId) {
        return {
          ...item,
          children: (item.children || []).filter(child => child.id !== itemId)
        };
      }
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: deleteChildFromItem(item.children, parentId, itemId)
        };
      }
      return item;
    });
  };

  const toggleExpand = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const moveItemUp = (index, parentId = null) => {
    if (index === 0) return;

    if (parentId) {
      setFormData({
        ...formData,
        items: moveChildUp(formData.items, parentId, index)
      });
    } else {
      const newItems = [...formData.items];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      setFormData({ ...formData, items: newItems });
    }
  };

  const moveItemDown = (index, parentId = null) => {
    if (parentId) {
      setFormData({
        ...formData,
        items: moveChildDown(formData.items, parentId, index)
      });
    } else {
      if (index === formData.items.length - 1) return;
      const newItems = [...formData.items];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      setFormData({ ...formData, items: newItems });
    }
  };

  const moveChildUp = (items, parentId, childIndex) => {
    return items.map(item => {
      if (!item) return item;
      if (item.id === parentId && item.children && item.children.length > childIndex) {
        const newChildren = [...item.children];
        if (childIndex > 0) {
          [newChildren[childIndex - 1], newChildren[childIndex]] = [newChildren[childIndex], newChildren[childIndex - 1]];
        }
        return { ...item, children: newChildren };
      }
      if (item.children && item.children.length > 0) {
        return { ...item, children: moveChildUp(item.children, parentId, childIndex) };
      }
      return item;
    });
  };

  const moveChildDown = (items, parentId, childIndex) => {
    return items.map(item => {
      if (!item) return item;
      if (item.id === parentId && item.children && item.children.length > childIndex + 1) {
        const newChildren = [...item.children];
        [newChildren[childIndex], newChildren[childIndex + 1]] = [newChildren[childIndex + 1], newChildren[childIndex]];
        return { ...item, children: newChildren };
      }
      if (item.children && item.children.length > 0) {
        return { ...item, children: moveChildDown(item.children, parentId, childIndex) };
      }
      return item;
    });
  };

  const renderMenuItem = (item, index, parentId = null, depth = 0) => {
    // Safety check for undefined items
    if (!item || !item.id) {
      console.error('Invalid menu item:', item);
      return null;
    }
    
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const isEditing = editingItemId === item.id;

    return (
      <div key={item.id} style={{ marginLeft: `${depth * 24}px` }}>
        <div className="flex items-center gap-2 py-2 px-3 hover:bg-gray-50 border-b group">
          <GripVertical className="h-4 w-4 text-gray-400 cursor-move flex-shrink-0" />
          
          {/* Expand/collapse button */}
          <button 
            onClick={() => toggleExpand(item.id)} 
            className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
            type="button"
          >
            {hasChildren && isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {/* Inline editing fields */}
          {isEditing ? (
            <>
              <Input
                value={item.title}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    items: updateItemInTree(formData.items, item.id, { title: e.target.value })
                  });
                }}
                className="flex-1 h-8 text-sm"
                placeholder="Title"
              />
              <Input
                value={item.url}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    items: updateItemInTree(formData.items, item.id, { url: e.target.value })
                  });
                }}
                className="flex-1 h-8 text-sm"
                placeholder="URL"
              />
              <Select 
                value={item.type} 
                onValueChange={(value) => {
                  setFormData({
                    ...formData,
                    items: updateItemInTree(formData.items, item.id, { type: value })
                  });
                }}
              >
                <SelectTrigger className="w-[120px] h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom</SelectItem>
                  <SelectItem value="page">Page</SelectItem>
                  <SelectItem value="collection">Collection</SelectItem>
                  <SelectItem value="product">Product</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingItemId(null)}
                className="h-7 w-7 p-0 flex-shrink-0"
                title="Done editing"
                type="button"
              >
                <Save className="h-3 w-3 text-green-600" />
              </Button>
            </>
          ) : (
            <>
              <div className="flex-1 flex items-center gap-2">
                <span className="text-sm font-medium">{item.title}</span>
                {item.url && <span className="text-xs text-gray-500">→ {item.url}</span>}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingItemId(item.id)}
                className="h-7 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                title="Edit"
                type="button"
              >
                Edit
              </Button>
            </>
          )}

          {/* Always visible "Add sub-item" button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => addNewItem(item.id)}
            className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            title="Add sub-item"
            type="button"
          >
            <Plus className="h-3 w-3 mr-1" />
            Sub
          </Button>

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => moveItemUp(index, parentId)}
              disabled={index === 0}
              className="h-7 px-2 text-xs disabled:opacity-30"
              title="Move up"
              type="button"
            >
              ↑
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => moveItemDown(index, parentId)}
              className="h-7 px-2 text-xs"
              title="Move down"
              type="button"
            >
              ↓
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => confirmDeleteItem(item.id, parentId)}
              className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
              title="Delete item"
              type="button"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div>
            {item.children.filter(child => child).map((child, childIndex) => renderMenuItem(child, childIndex, item.id, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/menus')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">{isEdit ? 'Edit menu' : 'Create menu'}</h1>
        </div>

        <div className="space-y-6">
          {/* Menu Details */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({
                      ...formData,
                      name,
                      handle: !isEdit ? generateHandle(name) : formData.handle
                    });
                  }}
                  placeholder="Main Menu"
                />
              </div>
              <div>
                <Label>Handle</Label>
                <Input value={formData.handle} disabled className="bg-gray-50" />
                <p className="text-xs text-gray-500 mt-1">
                  Used to reference this menu in your theme
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Menu Items */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-4">
                <Label className="text-lg font-semibold">Menu items</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addNewItem()}
                  type="button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add menu item
                </Button>
              </div>

              {formData.items.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                  <p className="text-gray-500 mb-4">No menu items yet</p>
                  <Button variant="outline" onClick={() => addNewItem()} type="button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add your first menu item
                  </Button>
                </div>
              ) : (
                <div className="border rounded-lg">
                  {formData.items.filter(item => item).map((item, index) => renderMenuItem(item, index))}
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-4">
                💡 Tip: Click &quot;Edit&quot; to modify a menu item inline, or hover to see all options
              </p>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => navigate('/menus')}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading || !formData.name.trim()}>
              {loading ? 'Saving...' : 'Save menu'}
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this menu item and all of its sub-items. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteConfirmed} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default MenuFormInline;
